import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useSettings } from './settings';

export type BrainSide = 'personal' | 'network' | 'both' | 'unknown';

// ── 两套菜单的模块 ID ──
// 简化版：四个动作（采集 / 知识地图 / 每日 / 问答）+ 社区/设置
// 工作台（10-05 立项）：主界面壳，单列一桶不靠模块归并
export type SimpleMenuId =
  | 'workbench'
  | 'ingest'
  | 'pipeline'
  | 'graph'
  | 'daily'
  | 'ask'
  | 'community'
  | 'settings';

// 经典版：完整 12 模块
export type ClassicMenuId =
  | 'workbench'
  | 'ingest'
  | 'graph'
  | 'cognitive'
  | 'emergence'
  | 'attention'
  | 'capsules'
  | 'knowledge'
  | 'pipeline'
  | 'social-brain'
  | 'embodied-cognition'
  | 'community'
  | 'settings';

export type MenuId = SimpleMenuId | ClassicMenuId;

export type SimpleTopNavBucketId = 'workbench' | 'collect' | 'process' | 'graph' | 'daily' | 'ask';
export type ClassicTopNavBucketId = 'workbench' | 'inbox' | 'process' | 'knowledge' | 'social';
export type TopNavBucketId = SimpleTopNavBucketId | ClassicTopNavBucketId;

export interface SubMenuItem {
  id: string;
  label: string;
  description: string;
  icon: string;
  path: string;
  brainSide?: BrainSide;
  preferredBrainSide?: BrainSide;
}

export interface MenuData {
  id: MenuId;
  label: string;
  icon: string;
  description: string;
  items: SubMenuItem[];
  defaultBrainSide: BrainSide;
  /**
   * 软隐藏：置 true 后该模块**不出现在任何导航里**，但代码、路由、按 ID 查元数据
   * （MENU_DATA）全部保留 —— 直接访问 URL 仍可用，恢复只需删掉这个标记。
   * 过滤点只在 `useMenuData()` 一处（渲染导航的唯一入口）。
   */
  hidden?: boolean;
}

export interface TopNavBucket {
  id: TopNavBucketId;
  label: string;
  icon: string;
  description: string;
  primaryModuleId: MenuId;
  moduleIds: MenuId[];
}

// ================================================================
// 简化版菜单（五动作）
// ================================================================

// ── 顶部导航：采集 / 自动理好 / 知识地图 / 每日 / 问答 五个动作 ──
export const TOP_NAV_BUCKETS_SIMPLE: TopNavBucket[] = [
  {
    id: 'workbench',
    label: '工作台',
    icon: 'LayoutDashboard',
    description: '神经图谱驾驶舱：流入待处理、选题池、对话一体',
    primaryModuleId: 'workbench',
    moduleIds: ['workbench'],
  },
  {
    id: 'collect',
    label: '采集',
    icon: 'Download',
    description: '笔记、剪藏、RSS、稍后读、文档等所有输入入口',
    primaryModuleId: 'ingest',
    moduleIds: ['ingest'],
  },
  {
    id: 'process',
    label: '自动理好',
    icon: 'Workflow',
    description: '把素材加工成可检索、可复用的知识卡片',
    primaryModuleId: 'pipeline',
    moduleIds: ['pipeline'],
  },
  {
    id: 'daily',
    label: '知识进化',
    icon: 'TrendingUp',
    description: '知识养成循环：争议→验证→践行→进化→回顾→被用',
    primaryModuleId: 'daily',
    moduleIds: ['daily'],
  },
  {
    id: 'graph',
    label: '知识地图',
    icon: 'Map',
    description: '知识图谱可视化：全局网络、路径、报告与时间轴',
    primaryModuleId: 'graph',
    moduleIds: ['graph'],
  },
  {
    id: 'ask',
    label: '问答',
    icon: 'Sparkles',
    description: '向你的知识库提问，答案必须带引用出处',
    primaryModuleId: 'ask',
    moduleIds: ['ask'],
  },
];

export const MENU_DATA_SIMPLE: Record<SimpleMenuId, MenuData> = {
  workbench: {
    id: 'workbench',
    label: '工作台',
    icon: 'LayoutDashboard',
    description: '神经图谱驾驶舱：流入待处理、选题池、对话一体',
    defaultBrainSide: 'both',
    items: [
      { id: 'home', label: '工作台首页', description: '神经粒子图谱、五卡、三态列表与对话', icon: 'LayoutDashboard', path: '/workbench', brainSide: 'both' },
    ],
  },
  ingest: {
    id: 'ingest',
    label: '采集',
    icon: 'Download',
    description: '把你的资料集中采集进来',
    defaultBrainSide: 'both',
    items: [
      { id: 'notes', label: '笔记管理', description: '笔记列表，按当前脑侧过滤', icon: 'FileText', path: '/ingest/notes', brainSide: 'both' },
      { id: 'sticky-notes', label: '便签墙', description: '随手记、提醒与彩色便签', icon: 'StickyNote', path: '/ingest/sticky-notes', brainSide: 'personal' },
      { id: 'clipper', label: '浏览器剪藏', description: '一键保存网页内容', icon: 'Globe', path: '/ingest/clipper', brainSide: 'network' },
      { id: 'batch-import', label: '批量导入', description: '批量导入笔记、剪藏和链接', icon: 'Upload', path: '/ingest/batch-import', brainSide: 'both' },
      { id: 'timeline', label: '时间轴', description: '按时间回顾全部内容与导入批次', icon: 'Clock', path: '/ingest/timeline', brainSide: 'both' },
      { id: 'tags', label: '标签档夹', description: '按标签自动归集的内容（无需手动整理）', icon: 'Tags', path: '/ingest/tags', brainSide: 'both' },
      { id: 'rss', label: 'RSS 聚合', description: '订阅源自动采集', icon: 'Rss', path: '/ingest/rss', brainSide: 'network' },
      { id: 'read-later', label: '稍后读', description: '收藏链接，稍后阅读', icon: 'BookOpen', path: '/ingest/read-later', brainSide: 'network' },
      { id: 'documents', label: '文件/文档库', description: '本地文档提取与管理', icon: 'FolderOpen', path: '/ingest/documents', brainSide: 'both' },
      { id: 'sources', label: '素材池', description: '跨工具共享的灵感素材', icon: 'Database', path: '/ingest/sources', brainSide: 'both' },
    ],
  },
  pipeline: {
    id: 'pipeline',
    label: '自动理好',
    icon: 'Workflow',
    description: '把原始素材加工成可用知识',
    defaultBrainSide: 'both',
    items: [
      { id: 'overview', label: '管线总览', description: '原始→卡片→抽取→碰撞→注卡', icon: 'Workflow', path: '/pipeline', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'raw', label: '原始素材', description: '未经处理的输入、剪藏、书摘', icon: 'Database', path: '/pipeline/raw', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'cards', label: '卡片化', description: '将素材切割为可复用卡片', icon: 'SquareStack', path: '/pipeline/cards', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'extract', label: '抽取', description: '提取概念、模型与行动建议', icon: 'Filter', path: '/pipeline/extract', brainSide: 'both', preferredBrainSide: 'both' },
      // 碰撞是生产线的工序（产出 collision_result 知识单元，下道是注卡）——属于本桶；
      // 它同时是「知识进化」链的第一环，跨桶跳转由 EvolutionChainBar 承担
      { id: 'collision', label: '碰撞', description: '跨领域连接与创意杂交', icon: 'Shuffle', path: '/pipeline/collision', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'annotate', label: '注卡', description: '为卡片注入个人语境与行动', icon: 'Pencil', path: '/pipeline/annotate', brainSide: 'personal', preferredBrainSide: 'personal' },
      // 涌现工作室不进简版（第六种动作会打散五动作主线）；只露画布——拖拽组合是
      // 管线加工的动手延伸，归本桶（08-22 拍板）。其余创意工具留经典版
      { id: 'canvas', label: '涌现画布', description: '拖拽组合创意想法', icon: 'Network', path: '/emergence/canvas', brainSide: 'both', preferredBrainSide: 'both' },
    ],
  },
  graph: {
    id: 'graph',
    label: '知识地图',
    icon: 'Map',
    description: '知识图谱可视化与结构洞察',
    defaultBrainSide: 'both',
    items: [
      { id: 'network', label: '知识网络', description: '全局关系图谱', icon: 'Network', path: '/graph/galaxy', brainSide: 'both' },
      { id: 'path', label: '路径探索', description: '发现知识路径', icon: 'Route', path: '/graph/path', brainSide: 'both' },
      { id: 'report', label: '图谱报告', description: '知识网络的统计与结构概览', icon: 'FileText', path: '/graph/report', brainSide: 'both' },
      { id: 'bridges', label: '跨脑桥梁', description: '连接个人脑与网络脑的关键关联', icon: 'GitMerge', path: '/graph/bridges', brainSide: 'both' },
      { id: 'tags', label: '标签图谱', description: '标签共现关系网络', icon: 'Tag', path: '/graph/tags', brainSide: 'both' },
      { id: 'wiki', label: '百科', description: '摄入时编译的主题条目', icon: 'BookOpen', path: '/graph/wiki', brainSide: 'both' },
      // 时间轴已搬到「采集」菜单（/ingest/timeline）：它现在是全量内容的批次回顾，不再是图谱节点视图
    ],
  },
  daily: {
    id: 'daily',
    label: '知识进化',
    icon: 'TrendingUp',
    description: '知识自进化全链路，顺序即流程',
    defaultBrainSide: 'both',
    items: [
      // 养成循环：争议→验证→践行→进化→回顾→被用（碰撞是生产工序，归「自动理好」；
      // 全链七步导航由 EvolutionChainBar 承担，本桶不含生产环节）
      { id: 'counter', label: '争议裁决', description: '有争议的知识在这里裁决：修正、保留或驳回', icon: 'XCircle', path: '/knowledge/counter', brainSide: 'both' },
      { id: 'verify', label: '验证中心', description: '多模型验证与共识裁决', icon: 'CheckCircle', path: '/knowledge/verify', brainSide: 'both' },
      { id: 'practice', label: '践行记录', description: '记录知识落地与验证', icon: 'Dumbbell', path: '/daily/practice-records', brainSide: 'both' },
      { id: 'evolution', label: '进化轨迹', description: '追踪知识从收集到内化', icon: 'TrendingUp', path: '/daily/evolution-track', brainSide: 'both' },
      { id: 'review', label: '每日复盘', description: '回顾今日输入与可信结论', icon: 'Calendar', path: '/daily', brainSide: 'both' },
      { id: 'invocation', label: '调用追踪', description: '统计知识被调用与践行次数', icon: 'Activity', path: '/daily/invocation-track', brainSide: 'both' },
      { id: 'health', label: '知识健康', description: '查看知识体系进化分布', icon: 'HeartPulse', path: '/daily/knowledge-health', brainSide: 'both' },
      { id: 'weekly', label: '每周回顾', description: '每周自动生成，事实与关联一网打尽', icon: 'FileText', path: '/daily/weekly-report', brainSide: 'both' },
      { id: 'challenge', label: '认知挑战', description: '每日思维训练打卡', icon: 'Gamepad2', path: '/daily/challenge', brainSide: 'both' },
    ],
  },
  ask: {
    id: 'ask',
    label: '问答',
    icon: 'Sparkles',
    description: '用 AI 向你的知识库提问',
    defaultBrainSide: 'both',
    items: [
      { id: 'query', label: 'AI 问答', description: '用自然语言提问，答案带引用', icon: 'Sparkles', path: '/graph/query', brainSide: 'both' },
      // 对话历史语义上属于「问答」（问答的沉淀），故挂在这个一级菜单下
      { id: 'chat-history', label: '对话历史', description: '问答会话沉淀，可继续聊、可存成知识', icon: 'MessageSquare', path: '/chat', brainSide: 'both' },
      { id: 'knowledge-network', label: '网络脑知识', description: '从外部采集的可验证知识', icon: 'Globe', path: '/knowledge/network', brainSide: 'network' },
      { id: 'knowledge-personal', label: '个人脑知识', description: '个人思考与沉淀的知识单元', icon: 'User', path: '/knowledge/personal', brainSide: 'personal' },
      // 验证中心/争议裁决已迁入「知识进化」链（一条链住一个家）
      { id: 'sources', label: '来源追溯', description: '按域名聚合的来源可信度', icon: 'GitCommit', path: '/knowledge/sources', brainSide: 'both' },
      // 瘦身（08-22 保守砍）：可信度地图与来源追溯同源（域名可信度的图视图）、未来的信
      // 语义不属于「问答」——砍掉入口，URL 仍可直达（只隐入口不隐路由）
    ],
  },
  community: {
    id: 'community',
    label: '社区',
    icon: 'MessageSquare',
    description: '用户交流区，登录后可发言',
    defaultBrainSide: 'both',
    items: [
      { id: 'posts', label: '社区动态', description: '浏览和发布社区消息', icon: 'MessageSquare', path: '/community', brainSide: 'both' },
      { id: 'guide', label: '使用指南', description: '从零上手：概念、流程、功能详解', icon: 'BookOpen', path: '/community/guide', brainSide: 'both' },
    ],
  },
  settings: {
    id: 'settings',
    label: '设置',
    icon: 'Settings',
    description: '系统配置与个性化',
    defaultBrainSide: 'both',
    items: [
      { id: 'account', label: '账户', description: '个人信息与密码', icon: 'User', path: '/settings/account', brainSide: 'both' },
      { id: 'privacy', label: '隐私', description: '数据安全设置', icon: 'Lock', path: '/settings/privacy', brainSide: 'both' },
      { id: 'ai', label: 'AI 设置', description: '模型与偏好配置', icon: 'Cpu', path: '/settings/ai', brainSide: 'both' },
      { id: 'byok', label: 'BYOK 自付', description: '供应商预设与 API Key 控制台', icon: 'KeyRound', path: '/settings/byok', brainSide: 'both' },
      { id: 'sync', label: '同步', description: '数据同步配置', icon: 'RefreshCw', path: '/settings/sync', brainSide: 'both' },
      { id: 'desktop', label: '桌面端', description: '桌面端下载与说明', icon: 'Monitor', path: '/settings/desktop', brainSide: 'both' },
      { id: 'storage', label: '存储', description: '数据打包与网盘备份', icon: 'HardDrive', path: '/settings/storage', brainSide: 'both' },
      { id: 'plugins', label: '插件', description: '扩展管理', icon: 'Puzzle', path: '/settings/plugins', brainSide: 'both' },
      { id: 'data', label: '数据', description: '导入导出与清理', icon: 'Database', path: '/settings/data', brainSide: 'both' },
      { id: 'appearance', label: '外观', description: '主题与显示', icon: 'Palette', path: '/settings/appearance', brainSide: 'both' },
      { id: 'recycle', label: '回收站', description: '30 天内可恢复已删内容', icon: 'Trash2', path: '/settings/recycle', brainSide: 'both' },
    ],
  },
};

export const QUICK_ACTIONS_SIMPLE: Pick<SubMenuItem, 'id' | 'label' | 'icon' | 'path' | 'brainSide'>[] = [
  { id: 'new-note', label: '新建笔记', icon: 'FileText', path: '/ingest/notes', brainSide: 'personal' },
  { id: 'new-clip', label: '剪藏网页', icon: 'Globe', path: '/ingest/clipper', brainSide: 'network' },
  { id: 'ai-query', label: 'AI 问答', icon: 'Sparkles', path: '/graph/query', brainSide: 'both' },
  { id: 'knowledge', label: '知识网络', icon: 'Network', path: '/graph/galaxy', brainSide: 'both' },
];

// ================================================================
// 经典版菜单（完整 12 模块，4 个一级导航桶）
// ================================================================

// ── 一级导航桶：把 12+ 个模块收敛成 4 个可理解的入口 ──
// “我/设置/会员/账单”统一放在左侧边栏底部菜单，不再占用顶部导航
export const TOP_NAV_BUCKETS_CLASSIC: TopNavBucket[] = [
  {
    id: 'workbench',
    label: '工作台',
    icon: 'LayoutDashboard',
    description: '神经图谱驾驶舱：流入待处理、选题池、对话一体',
    primaryModuleId: 'workbench',
    moduleIds: ['workbench'],
  },
  {
    id: 'inbox',
    label: '素材采集',
    icon: 'Download',
    description: '所有输入入口：笔记、剪藏、邮件、RSS、社交、稍后读、文档',
    primaryModuleId: 'ingest',
    moduleIds: ['ingest'],
  },
  {
    id: 'process',
    label: '素材加工',
    icon: 'Workflow',
    description: '对内容进行抽取、碰撞、创意和深度工作',
    primaryModuleId: 'pipeline',
    moduleIds: ['pipeline', 'attention', 'emergence'],
  },
  {
    id: 'knowledge',
    label: '知识库',
    icon: 'Shield',
    description: '沉淀下来的知识资产、关系图谱与时间胶囊',
    primaryModuleId: 'knowledge',
    moduleIds: ['knowledge', 'graph', 'capsules'],
  },
  {
    id: 'social',
    label: '社会大脑',
    icon: 'Users',
    description: '个人成长、社交、身体与情绪相关的工具',
    primaryModuleId: 'social-brain',
    moduleIds: ['social-brain', 'embodied-cognition', 'cognitive', 'community'],
  },
];

export const MENU_DATA_CLASSIC: Record<ClassicMenuId, MenuData> = {
  workbench: {
    id: 'workbench',
    label: '工作台',
    icon: 'LayoutDashboard',
    description: '神经图谱驾驶舱：流入待处理、选题池、对话一体',
    defaultBrainSide: 'both',
    items: [
      { id: 'home', label: '工作台首页', description: '神经粒子图谱、五卡、三态列表与对话', icon: 'LayoutDashboard', path: '/workbench', brainSide: 'both' },
    ],
  },
  ingest: {
    id: 'ingest',
    label: '采集',
    icon: 'Download',
    description: '从网络和个人来源收集信息',
    defaultBrainSide: 'network',
    items: [
      { id: 'sticky-notes', label: '便签墙', description: '随手记、提醒与彩色便签', icon: 'StickyNote', path: '/ingest/sticky-notes', brainSide: 'personal' },
      { id: 'clipper', label: '浏览器剪藏', description: '一键保存网页内容', icon: 'Globe', path: '/ingest/clipper', brainSide: 'network' },
      { id: 'bookmarks', label: '浏览器书签', description: '导入浏览器书签 HTML', icon: 'Bookmark', path: '/ingest/bookmarks', brainSide: 'network' },
      { id: 'notes', label: '笔记管理', description: '笔记列表，按当前脑侧过滤', icon: 'FileText', path: '/ingest/notes', brainSide: 'both' },
      { id: 'batch-import', label: '批量导入', description: '批量导入笔记、剪藏和链接', icon: 'Upload', path: '/ingest/batch-import', brainSide: 'both' },
      { id: 'timeline', label: '时间轴', description: '按时间回顾全部内容与导入批次', icon: 'Clock', path: '/ingest/timeline', brainSide: 'both' },
      { id: 'rss', label: 'RSS 聚合', description: '订阅源自动采集', icon: 'Rss', path: '/ingest/rss', brainSide: 'network' },
      { id: 'tags', label: '标签档夹', description: '按标签自动归集的内容（无需手动整理）', icon: 'Tags', path: '/ingest/tags', brainSide: 'both' },
      { id: 'email', label: '邮件集成', description: '邮件内容自动归档', icon: 'Mail', path: '/ingest/email', brainSide: 'network' },
      { id: 'social', label: '社交聚合', description: '社交媒体内容追踪', icon: 'MessageCircle', path: '/ingest/social', brainSide: 'network' },
      { id: 'read-later', label: '稍后读', description: '收藏链接，稍后阅读', icon: 'BookOpen', path: '/ingest/read-later', brainSide: 'network' },
      { id: 'documents', label: '文件/文档库', description: '本地文档提取与管理', icon: 'FolderOpen', path: '/ingest/documents', brainSide: 'both' },
    ],
  },
  graph: {
    id: 'graph',
    label: '图谱',
    icon: 'Network',
    description: '知识网络与关系可视化',
    defaultBrainSide: 'both',
    items: [
      { id: 'network', label: '知识网络', description: '全局关系图谱', icon: 'Network', path: '/graph/galaxy', brainSide: 'both' },
      { id: 'query', label: '智能查询', description: '用自然语言查询知识图谱', icon: 'Sparkles', path: '/graph/query', brainSide: 'both' },
      // 经典版没有「问答」桶：对话历史是问答的沉淀，挂在语义最近的「图谱」（智能查询旁）
      { id: 'chat-history', label: '对话历史', description: '问答会话沉淀，可继续聊、可存成知识', icon: 'MessageSquare', path: '/chat', brainSide: 'both' },
      { id: 'path', label: '路径探索', description: '发现知识路径', icon: 'Route', path: '/graph/path', brainSide: 'both' },
      { id: 'report', label: '图谱报告', description: '知识网络的统计与结构概览', icon: 'FileText', path: '/graph/report', brainSide: 'both' },
      { id: 'bridges', label: '跨脑桥梁', description: '连接个人脑与网络脑的关键关联', icon: 'GitMerge', path: '/graph/bridges', brainSide: 'both' },
      { id: 'tags', label: '标签图谱', description: '标签共现关系网络', icon: 'Tag', path: '/graph/tags', brainSide: 'both' },
      { id: 'wiki', label: '百科', description: '摄入时编译的主题条目', icon: 'BookOpen', path: '/graph/wiki', brainSide: 'both' },
      // 时间轴已搬到「采集」菜单（/ingest/timeline）：它现在是全量内容的批次回顾，不再是图谱节点视图
    ],
  },
  cognitive: {
    id: 'cognitive',
    label: '认知镜像',
    icon: 'Brain',
    description: '理解你的思维模式和认知偏差',
    defaultBrainSide: 'personal',
    items: [
      { id: 'fingerprint', label: '思维指纹', description: '个人思维特征分析', icon: 'Fingerprint', path: '/cognitive/fingerprint', brainSide: 'both' },
      { id: 'bias', label: '认知偏差', description: '识别思维盲区', icon: 'AlertTriangle', path: '/cognitive/bias', brainSide: 'both' },
      { id: 'conflict', label: '脑侧冲突', description: '个人脑与网络脑的张力', icon: 'Scale', path: '/cognitive/conflict', brainSide: 'both' },
      { id: 'audit', label: '决策审计', description: '追踪决策过程', icon: 'ClipboardCheck', path: '/cognitive/audit', brainSide: 'personal' },
      { id: 'simulate', label: '未来模拟', description: '决策结果预测', icon: 'GitBranch', path: '/cognitive/simulate', brainSide: 'personal' },
      { id: 'challenge', label: '认知挑战', description: '每日思维训练打卡', icon: 'Gamepad2', path: '/cognitive/challenge', brainSide: 'both' },
      { id: 'weekly-report', label: '每周回顾', description: '每周自动生成，事实与关联一网打尽', icon: 'FileText', path: '/cognitive/weekly-report', brainSide: 'both' },
    ],
  },
  emergence: {
    id: 'emergence',
    label: '涌现工作室',
    icon: 'Sparkles',
    description: '激发跨域创意与知识碰撞',
    defaultBrainSide: 'both',
    items: [
      { id: 'source-pool', label: '素材池', description: '跨工具共享的灵感素材', icon: 'Database', path: '/emergence/sources', brainSide: 'both' },
      { id: 'association', label: '跨域联想', description: '不同领域知识连接', icon: 'Shuffle', path: '/emergence/associate', brainSide: 'both' },
      { id: 'collision', label: '创意碰撞', description: '多角度观点碰撞', icon: 'Zap', path: '/emergence/collision', brainSide: 'both' },
      { id: 'hybrid', label: '概念杂交', description: '融合生成新概念', icon: 'Combine', path: '/emergence/hybrid', brainSide: 'both' },
      { id: 'counterfactual', label: '反事实探索', description: '假设情景推演', icon: 'HelpCircle', path: '/emergence/counterfactual', brainSide: 'both' },
      { id: 'canvas', label: '涌现画布', description: '拖拽组合创意想法', icon: 'Network', path: '/emergence/canvas', brainSide: 'both' },
      { id: 'library', label: '成果库', description: '保存与转化创意成果', icon: 'BookOpen', path: '/emergence/library', brainSide: 'both' },
    ],
  },
  attention: {
    id: 'attention',
    label: '注意力管家',
    icon: 'Target',
    description: '管理你的注意力资源',
    defaultBrainSide: 'both',
    items: [
      { id: 'dashboard', label: '仪表盘', description: '双脑注意力总览', icon: 'PieChart', path: '/attention/dashboard', brainSide: 'both' },
      { id: 'deep-work', label: '深度工作', description: '个人脑专注时段管理', icon: 'Timer', path: '/attention/deep-work', brainSide: 'personal' },
      { id: 'budget', label: '时间预算', description: '个人脑时间投入规划', icon: 'Wallet', path: '/attention/budget', brainSide: 'personal' },
      { id: 'guardian', label: '干扰守门员', description: '网络脑入口控制', icon: 'Shield', path: '/attention/guardian', brainSide: 'network' },
      { id: 'ration', label: '信息流配给', description: '网络脑内容消费限额', icon: 'Newspaper', path: '/attention/ration', brainSide: 'network' },
      { id: 'stats', label: '统计分析', description: '跨脑侧注意力模式洞察', icon: 'TrendingUp', path: '/attention/stats', brainSide: 'both' },
    ],
  },
  capsules: {
    id: 'capsules',
    label: '时间胶囊',
    icon: 'Package',
    description: '封存记忆，未来开启',
    defaultBrainSide: 'both',
    items: [
      { id: 'my-capsules', label: '我的胶囊', description: '个人创建与收藏的胶囊', icon: 'List', path: '/capsules/my', brainSide: 'personal' },
      { id: 'create', label: '创建胶囊', description: '封存新的记忆', icon: 'Plus', path: '/capsules/create', brainSide: 'personal' },
      { id: 'dialogue', label: '时光对话', description: '与过去自己的跨时空对话', icon: 'MessageCircle', path: '/capsules/dialogue', brainSide: 'personal' },
      { id: 'plaza', label: '胶囊广场', description: '公开与共享的胶囊广场', icon: 'Globe', path: '/capsules/plaza', brainSide: 'network' },
      { id: 'schedule', label: '解锁日程', description: '按时间线查看待解锁胶囊', icon: 'Calendar', path: '/capsules/schedule', brainSide: 'both' },
      { id: 'stats', label: '胶囊统计', description: '双脑胶囊数据分析', icon: 'BarChart2', path: '/capsules/stats', brainSide: 'both' },
    ],
  },
  knowledge: {
    id: 'knowledge',
    label: '反脆弱知识库',
    icon: 'Shield',
    description: '可验证、可追溯、可进化的知识体系',
    defaultBrainSide: 'both',
    items: [
      { id: 'network', label: '网络脑知识', description: '从外部采集的可验证知识', icon: 'Globe', path: '/knowledge/network', brainSide: 'network' },
      { id: 'personal', label: '个人脑知识', description: '个人思考与沉淀的知识单元', icon: 'User', path: '/knowledge/personal', brainSide: 'personal' },
      { id: 'verify', label: '验证中心', description: '多模型验证与共识裁决', icon: 'CheckCircle', path: '/knowledge/verify', brainSide: 'both' },
      { id: 'counter', label: '争议裁决', description: '有争议的知识在这里裁决：修正、保留或驳回', icon: 'XCircle', path: '/knowledge/counter', brainSide: 'both' },
      { id: 'sources', label: '来源追溯', description: '按域名聚合的来源可信度', icon: 'GitCommit', path: '/knowledge/sources', brainSide: 'both' },
      { id: 'credibility', label: '可信度地图', description: '来源域名的可信度分布', icon: 'Map', path: '/knowledge/credibility', brainSide: 'both' },
      { id: 'timeliness', label: '时效性监测', description: '知识过期与半衰期追踪', icon: 'Clock', path: '/knowledge/timeliness', brainSide: 'both' },
      { id: 'stats', label: '统计洞察', description: '双脑知识健康度分析', icon: 'Activity', path: '/knowledge/stats', brainSide: 'both' },
    ],
  },
  pipeline: {
    id: 'pipeline',
    label: '知识流水线',
    icon: 'Workflow',
    description: '知识不是仓库，是一条阶段化生产线',
    defaultBrainSide: 'both',
    items: [
      { id: 'overview', label: '管线总览', description: '原始→卡片→抽取→碰撞→注卡', icon: 'Workflow', path: '/pipeline', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'raw', label: '原始素材', description: '未经处理的输入、剪藏、书摘', icon: 'Database', path: '/pipeline/raw', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'cards', label: '卡片化', description: '将素材切割为可复用卡片', icon: 'SquareStack', path: '/pipeline/cards', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'extract', label: '抽取', description: '提取概念、模型与行动建议', icon: 'Filter', path: '/pipeline/extract', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'collision', label: '碰撞', description: '跨领域连接与创意杂交', icon: 'Shuffle', path: '/pipeline/collision', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'annotate', label: '注卡', description: '为卡片注入个人语境与行动', icon: 'Pencil', path: '/pipeline/annotate', brainSide: 'personal', preferredBrainSide: 'personal' },
    ],
  },
  'social-brain': {
    id: 'social-brain',
    label: '社会大脑',
    icon: 'Users',
    description: '个体认知与群体智慧的连接',
    defaultBrainSide: 'both',
    items: [
      { id: 'ai-context', label: 'AI全知上下文', description: '让AI基于引导文件理解你的知识库', icon: 'BrainCircuit', path: '/social-brain/ai-context', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'cognitive-potential', label: '认知资产', description: '能下沉、能产出、能变现的认知资产', icon: 'Zap', path: '/social-brain/cognitive-potential', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'experimenter', label: '单变量实验', description: '每次只控制一个变量，用反馈迭代', icon: 'FlaskConical', path: '/social-brain/experimenter', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'daily-review', label: '每日复盘', description: '回顾今日输入，发现行为差距', icon: 'Calendar', path: '/social-brain/daily-review', brainSide: 'both', preferredBrainSide: 'both' },
      // 自进化链（经典版）：复盘→践行→进化→被用→健康 按链路序相邻；
      // 碰撞在素材加工桶、验证/争议在知识库桶相邻——链内跳转靠 EvolutionChainBar
      { id: 'practice-records', label: '践行记录', description: '记录知识落地与验证', icon: 'Dumbbell', path: '/social-brain/practice-records', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'evolution-track', label: '进化轨迹', description: '追踪知识从收集到内化', icon: 'TrendingUp', path: '/social-brain/evolution-track', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'invocation-track', label: '调用追踪', description: '统计知识被调用与践行次数', icon: 'Activity', path: '/social-brain/invocation-track', brainSide: 'both', preferredBrainSide: 'both' },
      { id: 'knowledge-health', label: '知识健康', description: '查看知识体系进化分布', icon: 'HeartPulse', path: '/social-brain/knowledge-health', brainSide: 'both', preferredBrainSide: 'both' },
    ],
  },
  'embodied-cognition': {
    id: 'embodied-cognition',
    label: '具身认知',
    icon: 'Heart',
    description: '身体、情绪与环境作为记忆载体',
    defaultBrainSide: 'personal',
    // 软隐藏（产品决定）：从一级菜单撤下，但路由与页面全部保留，
    // 直接访问 /embodied-cognition/* 仍可用；恢复=删掉这一行。
    hidden: true,
    items: [
      { id: 'depth-check', label: '内容深度评估', description: '保存时自动评估内容深度；可切换 AI 深度评估', icon: 'ShieldAlert', path: '/embodied-cognition/depth-check', brainSide: 'both' },
      { id: 'true-evolution', label: '真成长 vs 伪熟练', description: '进化=摩擦+痛苦后的喜悦', icon: 'TrendingUp', path: '/embodied-cognition/true-evolution', brainSide: 'both' },
      { id: 'mood-location', label: '情绪与环境', description: '胶囊中的mood、location、身体状态', icon: 'MapPin', path: '/embodied-cognition/mood-location', brainSide: 'both' },
    ],
  },
  community: {
    id: 'community',
    label: '社区',
    icon: 'MessageSquare',
    description: '用户交流区，登录后可发言',
    defaultBrainSide: 'both',
    items: [
      { id: 'posts', label: '社区动态', description: '浏览和发布社区消息', icon: 'MessageSquare', path: '/community', brainSide: 'both' },
      { id: 'guide', label: '使用指南', description: '从零上手：概念、流程、功能详解', icon: 'BookOpen', path: '/community/guide', brainSide: 'both' },
    ],
  },
  settings: {
    id: 'settings',
    label: '设置',
    icon: 'Settings',
    description: '系统配置与个性化',
    defaultBrainSide: 'both',
    items: [
      { id: 'account', label: '账户', description: '个人信息与密码', icon: 'User', path: '/settings/account', brainSide: 'both' },
      { id: 'privacy', label: '隐私', description: '数据安全设置', icon: 'Lock', path: '/settings/privacy', brainSide: 'both' },
      { id: 'ai', label: 'AI 设置', description: '模型与偏好配置', icon: 'Cpu', path: '/settings/ai', brainSide: 'both' },
      { id: 'byok', label: 'BYOK 自付', description: '供应商预设与 API Key 控制台', icon: 'KeyRound', path: '/settings/byok', brainSide: 'both' },
      { id: 'sync', label: '同步', description: '数据同步配置', icon: 'RefreshCw', path: '/settings/sync', brainSide: 'both' },
      { id: 'desktop', label: '桌面端', description: '桌面端下载与说明', icon: 'Monitor', path: '/settings/desktop', brainSide: 'both' },
      { id: 'storage', label: '存储', description: '数据打包与网盘备份', icon: 'HardDrive', path: '/settings/storage', brainSide: 'both' },
      { id: 'plugins', label: '插件', description: '扩展管理', icon: 'Puzzle', path: '/settings/plugins', brainSide: 'both' },
      { id: 'data', label: '数据', description: '导入导出与清理', icon: 'Database', path: '/settings/data', brainSide: 'both' },
      { id: 'appearance', label: '外观', description: '主题与显示', icon: 'Palette', path: '/settings/appearance', brainSide: 'both' },
      { id: 'recycle', label: '回收站', description: '30 天内可恢复已删内容', icon: 'Trash2', path: '/settings/recycle', brainSide: 'both' },
    ],
  },
};

export const QUICK_ACTIONS_CLASSIC: Pick<SubMenuItem, 'id' | 'label' | 'icon' | 'path' | 'brainSide'>[] = [
  { id: 'new-note', label: '新建笔记', icon: 'FileText', path: '/ingest/notes', brainSide: 'personal' },
  { id: 'new-capsule', label: '创建胶囊', icon: 'Package', path: '/capsules/create', brainSide: 'personal' },
  { id: 'start-focus', label: '深度工作', icon: 'Target', path: '/attention/deep-work', brainSide: 'personal' },
  { id: 'new-clip', label: '剪藏网页', icon: 'Globe', path: '/ingest/clipper', brainSide: 'network' },
  { id: 'knowledge', label: '知识库', icon: 'BookOpen', path: '/knowledge/network', brainSide: 'network' },
  { id: 'verify', label: '验证中心', icon: 'CheckCircle', path: '/knowledge/verify', brainSide: 'both' },
];

// ================================================================
// 模式无关的兼容导出与工具函数
// ================================================================

// 合并查找表：仅供按 ID 查模块元数据（如 defaultBrainSide），不用于渲染导航
export const MENU_DATA: Record<MenuId, MenuData> = {
  ...MENU_DATA_CLASSIC,
  ...MENU_DATA_SIMPLE,
};

/**
 * 软隐藏过滤：剔除打了 `hidden: true` 的模块，并把它们从一级桶的 moduleIds 里摘掉。
 * 只在 useMenuData() 里调用 —— 那是渲染导航的唯一入口，所以一处生效、全站一致。
 * 按 ID 查元数据（MENU_DATA / getMenuIdByPath）**不过滤**，保证直接访问 URL 仍能定位模块。
 */
function visibleMenu(menu: Record<string, MenuData>): Record<MenuId, MenuData> {
  const out: Record<string, MenuData> = {};
  for (const [id, mod] of Object.entries(menu)) {
    if (!mod.hidden) out[id] = mod;
  }
  return out as Record<MenuId, MenuData>;
}

function hiddenMenuIds(): Set<string> {
  return new Set(
    Object.values(MENU_DATA)
      .filter((m) => m.hidden)
      .map((m) => m.id as string)
  );
}

function visibleBuckets(buckets: TopNavBucket[]): TopNavBucket[] {
  const hidden = hiddenMenuIds();
  return buckets
    .map((b) => ({ ...b, moduleIds: b.moduleIds.filter((id) => !hidden.has(id as string)) }))
    .filter((b) => b.moduleIds.length > 0);
}

/** 按当前界面版本返回导航数据（渲染导航一律用这个 hook） */
export function useMenuData() {
  const uiMode = useSettings((s) => s.uiMode);
  if (uiMode === 'classic') {
    return {
      menuData: visibleMenu(MENU_DATA_CLASSIC),
      topNavBuckets: visibleBuckets(TOP_NAV_BUCKETS_CLASSIC),
      quickActions: QUICK_ACTIONS_CLASSIC,
    };
  }
  return {
    menuData: visibleMenu(MENU_DATA_SIMPLE),
    topNavBuckets: visibleBuckets(TOP_NAV_BUCKETS_SIMPLE),
    quickActions: QUICK_ACTIONS_SIMPLE,
  };
}

// ── 设置菜单：Sidebar 和 SettingsPage 共用（两版一致） ──
export const SETTINGS_ITEMS: Pick<SubMenuItem, 'id' | 'label' | 'icon' | 'path'>[] = [
  { id: 'account', label: '账户', icon: 'User', path: '/settings/account' },
  { id: 'privacy', label: '隐私', icon: 'Lock', path: '/settings/privacy' },
  { id: 'ai', label: 'AI 设置', icon: 'Cpu', path: '/settings/ai' },
  { id: 'byok', label: 'BYOK 自付', icon: 'KeyRound', path: '/settings/byok' },
  { id: 'sync', label: '同步', icon: 'RefreshCw', path: '/settings/sync' },
  { id: 'desktop', label: '桌面端', icon: 'Monitor', path: '/settings/desktop' },
  { id: 'storage', label: '存储', icon: 'HardDrive', path: '/settings/storage' },
  { id: 'plugins', label: '插件', icon: 'Puzzle', path: '/settings/plugins' },
  { id: 'data', label: '数据', icon: 'Database', path: '/settings/data' },
  { id: 'appearance', label: '外观', icon: 'Palette', path: '/settings/appearance' },
  { id: 'recycle', label: '回收站', icon: 'Trash2', path: '/settings/recycle' },
];

// ── 路径 → 模块 ID（menuData 传当前模式的菜单表；默认合并表） ──
export function getMenuIdByPath(
  pathname: string,
  menuData: Record<string, MenuData> = MENU_DATA
): MenuId | null {
  // 先按菜单项路径匹配（最长优先）：二级菜单允许跨路径前缀调动
  // （如简化版「问答」桶里的 /graph/query），归属以菜单配置为准
  let best: { id: MenuId; len: number } | null = null;
  for (const [id, mod] of Object.entries(menuData)) {
    for (const item of mod.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        if (!best || item.path.length > best.len) best = { id: id as MenuId, len: item.path.length };
      }
    }
  }
  if (best) return best.id;

  const segments = pathname.toLowerCase().split('/').filter(Boolean);
  const first = segments[0] as MenuId | string;
  if (menuData[first as MenuId]) return first as MenuId;
  // 个人中心相关独立页面统一归入 settings 桶
  if (['payment', 'topup', 'billing', 'business-plan'].includes(first)) return 'settings';
  return null;
}

// ── 模块 ID → 一级导航桶（buckets 传当前模式的桶列表；默认经典版） ──
export function getBucketByMenuId(
  menuId: MenuId | null,
  buckets: TopNavBucket[] = TOP_NAV_BUCKETS_CLASSIC
): TopNavBucket | undefined {
  if (!menuId) return undefined;
  return buckets.find((b) => b.moduleIds.includes(menuId));
}

// ── 脑侧过滤通用逻辑 ──
export function getVisibleItems<T extends { brainSide?: BrainSide }>(
  items: T[],
  brainSide: BrainSide
): T[] {
  if (brainSide === 'both' || brainSide === 'unknown' || !brainSide) return items;
  return items.filter((item) => {
    if (!item.brainSide || item.brainSide === 'both') return true;
    return item.brainSide === brainSide;
  });
}

interface NavigationState {
  activeMenu: MenuId | null;
  subMenuOpen: boolean;
  brainSide: BrainSide;
  setActiveMenu: (menu: MenuId | null) => void;
  toggleSubMenu: () => void;
  setBrainSide: (side: BrainSide) => void;
  closeSubMenu: () => void;
}

export const useNavigation = create<NavigationState>()(
  persist(
    (set) => ({
      activeMenu: null,
      subMenuOpen: false,
      brainSide: 'both',
      setActiveMenu: (menu) =>
        set((state) => ({
          activeMenu: menu,
          subMenuOpen: menu !== null ? true : state.subMenuOpen,
          brainSide: menu ? MENU_DATA[menu].defaultBrainSide : state.brainSide,
        })),
      toggleSubMenu: () => set((state) => ({ subMenuOpen: !state.subMenuOpen })),
      setBrainSide: (side) => set({ brainSide: side }),
      closeSubMenu: () => set({ subMenuOpen: false }),
    }),
    {
      name: 'psb-navigation',
      partialize: (state) => ({ brainSide: state.brainSide }),
    }
  )
);
